// src/pages/Tour/EndKm.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import tourService from '../../services/tour.service';
import Toast from '../../components/Toast/Toast';
import './TourPages.css';

const EndKm = () => {
  const navigate = useNavigate();

  const [todayTour, setTodayTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showConfirm, setShowConfirm] = useState(false); // two-step confirm
  const [previewTotalKm, setPreviewTotalKm] = useState(null);

  const [formData, setFormData] = useState({
    endKm: '',
    endLatitude: '',
    endLongitude: '',
  });

 useEffect(() => {
  const fetchTodayTour = async () => {
    try {
      const response = await tourService.getTodayTour();
      if (response.success && response.data) {
        setTodayTour(response.data);
      }
    } catch (error) {
      console.error('Error fetching today tour:', error);
      setToast({ 
        message: 'Failed to fetch today\'s tour. Please refresh.', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };
  fetchTodayTour();
}, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const detectLocation = () => {
  if (!navigator.geolocation) {
    setToast({ message: 'Geolocation is not supported by your browser.', type: 'error' });
    return;
  }

  setToast({ message: 'Detecting location…', type: 'info' });

  let bestReading = null;
  let settled = false;

 const finish = (position) => {
  if (settled) return;
  settled = true;
  navigator.geolocation.clearWatch(watchId);

  const { latitude, longitude, accuracy } = position.coords;
  setFormData(prev => ({
    ...prev,
    endLatitude: latitude.toFixed(8),      // was: latitude
    endLongitude: longitude.toFixed(8),    // was: longitude
    endLocationAccuracy: accuracy,         // was: locationAccuracy
  }));

  setToast({ message: '📍 Location detected!', type: 'success' });
};

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
        bestReading = position;
      }
      // good enough — stop early
      if (position.coords.accuracy <= 15) {
        finish(position);
      }
    },
    (error) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      const message =
        error.code === error.PERMISSION_DENIED
          ? 'Location access denied. Please enable it in your browser/app settings.'
          : error.code === error.TIMEOUT
          ? 'Location request timed out. Please try again.'
          : 'Unable to detect location. ';
      setToast({ message, type: 'error' });
      console.error('Geolocation error:', error);
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );

  // Hard stop after ~12s — use best reading we've got rather than hang forever
  setTimeout(() => {
    if (!settled && bestReading) {
      finish(bestReading);
    } else if (!settled) {
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      setToast({ message: 'Could not get an accurate location. ', type: 'error' });
    }
  }, 12000);
};

  const validate = () => {
    const newErrors = {};
    if (formData.endKm === '' || formData.endKm === null || formData.endKm === undefined) {
  newErrors.endKm = 'End KM is required';
} else if (isNaN(formData.endKm) || parseInt(formData.endKm) < 0) {
  newErrors.endKm = 'Enter a valid KM value';
} else if (todayTour?.startKm !== undefined && parseInt(formData.endKm) < todayTour.startKm) {
  newErrors.endKm = `End KM must be ≥ Start KM (${todayTour.startKm})`;
}
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 1: validate and show confirm screen
  const handleReview = () => {
    if (!validate()) return;
    setPreviewTotalKm(parseInt(formData.endKm) - (todayTour.startKm || 0));
    setShowConfirm(true);
  };

  // Step 2: final submit — locked after this
  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        endKm: parseInt(formData.endKm),
        endLatitude: formData.endLatitude || undefined,
        endLongitude: formData.endLongitude || undefined,
      };
      const response = await tourService.endKm(payload);
      if (response.success) {
        setToast({ message: 'Tour completed successfully!', type: 'success' });
        setTimeout(() => navigate('/tour/monthly'), 1500);
      }
    } catch (error) {
      setShowConfirm(false); // go back to form on error
      setToast({
        message: error.response?.data?.message || 'Failed to submit End KM',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="tour-page-container">
        <div className="tour-loading">Checking today's tour status...</div>
      </div>
    );
  }

  return (
    <div className="tour-page-container">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Breadcrumb */}
      <nav className="tour-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a>
        <span>›</span>
        <span>Tour</span>
        <span>›</span>
        <span>End KM</span>
      </nav>

      {/* Header */}
      <div className="tour-header">
        <h4 className="tour-title">End KM</h4>
        <p className="tour-subtitle">Submit your tour end details for the day</p>
      </div>

      {/* No start KM today */}
      {!todayTour ? (
        <div className="tour-locked-card">
          <div className="tour-locked-icon">⚠️</div>
          <h5 className="tour-locked-title">No Tour Started Today</h5>
          <p className="tour-locked-text">
            You need to submit Start KM before submitting End KM.
          </p>
          <div className="tour-form-actions" style={{ justifyContent: 'center', borderTop: 'none', marginTop: 20 }}>
            <button
              className="btn-tour-primary"
              onClick={() => navigate('/tour/start-km')}
            >
              Go to Start KM
            </button>
          </div>
        </div>
      ) : todayTour.status === 'COMPLETED' ? (
        /* Already completed */
        <div className="tour-locked-card">
          <div className="tour-locked-icon">✅</div>
          <h5 className="tour-locked-title">Tour Already Completed</h5>
          <p className="tour-locked-text">
            End KM has already been submitted for today. Once submitted, it cannot be edited.
          </p>
          <div className="tour-locked-detail">
            <div className="tour-detail-row">
              <span className="tour-detail-label">Start KM</span>
              <span className="tour-detail-value">{todayTour.startKm} km</span>
            </div>
            <div className="tour-detail-row">
              <span className="tour-detail-label">End KM</span>
              <span className="tour-detail-value">{todayTour.endKm} km</span>
            </div>
            <div className="tour-detail-row">
              <span className="tour-detail-label">Total KM</span>
              <span className="tour-detail-value">{todayTour.totalKm} km</span>
            </div>
          </div>
        </div>
      ) : todayTour.tourType !== 'WORKING' ? (
        /* Leave/Campaigning day */
        <div className="tour-locked-card">
          <div className="tour-locked-icon">📋</div>
          <h5 className="tour-locked-title">
            {todayTour.tourType === 'LEAVE' ? 'Leave Day' : 'Campaigning Day'}
          </h5>
          <p className="tour-locked-text">
            End KM is not required for{' '}
            {todayTour.tourType === 'LEAVE' ? 'leave' : 'campaigning'} days.
          </p>
        </div>
      ) : (
        /* Show End KM form OR confirm screen */
        <>
          {/* Today's start details banner */}
          <div className="tour-status-banner started" style={{ marginBottom: 20 }}>
            <span className="status-dot green" />
            Tour started today — Start KM: <strong>{todayTour.startKm}</strong>
            {todayTour.startDestination && (
              <> &nbsp;|&nbsp; Destination: <strong>{todayTour.startDestination}</strong></>
            )}
          </div>

          {!showConfirm ? (
            /* ── Step 1: Fill form ── */
            <div className="tour-form-card">
              <p className="form-section-title">End Tour Details</p>

              {/* End KM + live preview */}
              <div className="tour-form-row">
                <div className="tour-form-group">
                  <label className="tour-label">
                    End KM <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    name="endKm"
                    className={`tour-input ${errors.endKm ? 'error' : ''}`}
                    value={formData.endKm}
                    onChange={handleChange}
                    placeholder={`Enter end km (min: ${todayTour.startKm})`}
                    min={todayTour.startKm || 0}
                  />
                  {errors.endKm && (
                    <span className="tour-error-message">{errors.endKm}</span>
                  )}
                </div>

                {/* Live total KM preview */}
                {formData.endKm &&
                  !errors.endKm &&
                  parseInt(formData.endKm) >= (todayTour.startKm || 0) && (
                  <div className="tour-form-group">
                    <label className="tour-label">Total KM (Preview)</label>
                    <div
                      className="tour-input"
                      style={{
                        background: '#f8fdf5',
                        border: '1.5px solid #aed581',
                        color: '#2e7d32',
                        fontWeight: 700,
                        fontSize: 16,
                      }}
                    >
                      {parseInt(formData.endKm) - (todayTour.startKm || 0)} km
                    </div>
                  </div>
                )}
              </div>

              {/* Location */}
              <div className="tour-form-row">
                <div className="tour-form-group">
                  <label className="tour-label">Latitude</label>
                  <div className="location-input-group">
                    <input
                      type="text"
                      name="endLatitude"
                      className="tour-input"
                      value={formData.endLatitude}
                      onChange={handleChange}
                      placeholder="Enter latitude"
                    />
                    <button type="button" className="btn-detect" onClick={detectLocation}>
                      📍 Detect
                    </button>
                  </div>
                </div>

                <div className="tour-form-group">
                  <label className="tour-label">Longitude</label>
                  <input
                    type="text"
                    name="endLongitude"
                    className="tour-input"
                    value={formData.endLongitude}
                    onChange={handleChange}
                    placeholder="Enter longitude"
                  />
                </div>
              </div>

              {/* Step 1 Actions */}
              <div className="tour-form-actions">
                <button
                  type="button"
                  className="btn-tour-secondary"
                  onClick={() => navigate('/tour/monthly')}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-tour-primary"
                  onClick={handleReview}
                >
                  Review →
                </button>
              </div>
            </div>
          ) : (
            /* ── Step 2: Confirm before locking ── */
            <div
              className="tour-form-card"
              style={{ border: '1.5px solid #aed581', background: '#f8fdf5' }}
            >
              <p className="form-section-title" style={{ color: '#2e7d32' }}>
                Review & Confirm
              </p>

              {/* Warning */}
              <div
                className="tour-status-banner none"
                style={{ marginBottom: 20 }}
              >
                <span className="status-dot amber" />
                ⚠️ Once submitted, End KM <strong>cannot be edited</strong>. Please review carefully.
              </div>

              {/* Summary */}
              <div className="tour-locked-detail" style={{ width: '100%' }}>
                <div className="tour-detail-row">
                  <span className="tour-detail-label">Tour Date</span>
                  <span className="tour-detail-value">
                    {new Date(todayTour.tourDate).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </span>
                </div>
                <div className="tour-detail-row">
                  <span className="tour-detail-label">Start KM</span>
                  <span className="tour-detail-value">{todayTour.startKm} km</span>
                </div>
                <div className="tour-detail-row">
                  <span className="tour-detail-label">End KM</span>
                  <span className="tour-detail-value">{formData.endKm} km</span>
                </div>
                <div className="tour-detail-row">
                  <span className="tour-detail-label">Total KM</span>
                  <span
                    className="tour-detail-value"
                    style={{ color: '#2e7d32', fontWeight: 700, fontSize: 16 }}
                  >
                    {previewTotalKm} km
                  </span>
                </div>
                {todayTour.modeOfTransport && (
                  <div className="tour-detail-row">
                    <span className="tour-detail-label">Transport</span>
                    <span className="tour-detail-value">
                      {todayTour.modeOfTransport.replace('_', ' ')}
                    </span>
                  </div>
                )}
                {formData.endLatitude && (
                  <div className="tour-detail-row">
                    <span className="tour-detail-label">Location</span>
                    <span className="tour-detail-value">
                      {formData.endLatitude}, {formData.endLongitude}
                    </span>
                  </div>
                )}
              </div>

              {/* Step 2 Actions */}
              <div className="tour-form-actions">
                <button
                  type="button"
                  className="btn-tour-secondary"
                  onClick={() => setShowConfirm(false)}
                  disabled={isSubmitting}
                >
                  ← Edit
                </button>
                <button
                  type="button"
                  className="btn-tour-primary"
                  onClick={handleConfirmSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Confirm & Submit'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EndKm;